import type React from 'react';
import { useRef, useState } from 'react';

import type { VideoStory } from '#src/app/pages/about/impact/pageContext';

/** Minimum horizontal travel (px) for a pointer gesture to count as a swipe. */
const SWIPE_THRESHOLD = 40;
/** How long the click fired at the end of a swipe gesture is ignored for. */
const SWIPE_CLICK_GUARD_MS = 50;

type Result = ModelHookResult<
	{
		activeIndex: number;
		/** Per-slide counter keyed into the embed: bumping it remounts that slide's iframe. */
		reloadCounts: readonly number[];
		count: number;
	},
	{
		next: () => void;
		prev: () => void;
		goTo: (index: number) => void;
		onPointerDown: (event: Pick<React.PointerEvent, 'clientX'>) => void;
		onPointerUp: (event: Pick<React.PointerEvent, 'clientX'>) => void;
	}
>;

export function useVideoCarousel(stories: readonly VideoStory[]): Result {
	const count = stories.length;
	const [activeIndex, setActiveIndex] = useState(0);
	const [reloadCounts, setReloadCounts] = useState<readonly number[]>(() => stories.map(() => 0));
	// pointer coordinates are transient gesture state, never rendered
	const pointerStartXRef = useRef<number | null>(null);
	const swipedRef = useRef(false);

	const changeActiveIndex = (index: number) => {
		if (index === activeIndex) return;
		// reloading the slide being left is what stops any playback in it
		setReloadCounts((counts) => counts.map((reloadCount, i) => (i === activeIndex ? reloadCount + 1 : reloadCount)));
		setActiveIndex(index);
	};

	const next = () => changeActiveIndex((activeIndex + 1) % count);
	const prev = () => changeActiveIndex((activeIndex - 1 + count) % count);

	return {
		data: { activeIndex, reloadCounts, count },
		actions: {
			next,
			prev,
			// the click that ends a swipe lands on a slide; the swipe already moved the carousel
			goTo: (index: number) => {
				if (swipedRef.current) return;
				changeActiveIndex(index);
			},
			onPointerDown: ({ clientX }) => {
				pointerStartXRef.current = clientX;
			},
			onPointerUp: ({ clientX }) => {
				const startX = pointerStartXRef.current;
				pointerStartXRef.current = null;
				if (startX === null) return;

				const delta = clientX - startX;
				if (Math.abs(delta) <= SWIPE_THRESHOLD) return;

				if (delta < 0) next();
				else prev();

				swipedRef.current = true;
				window.setTimeout(() => {
					swipedRef.current = false;
				}, SWIPE_CLICK_GUARD_MS);
			},
		},
	};
}
